import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { api, fcfa } from '../lib/api';
import { useI18n } from '../lib/i18n';
import { Gavel, Zap, Copy, Check, Wallet, Smartphone, ShieldCheck } from 'lucide-react';

export default function PaymentBlock({ order, onSubmitted }) {
  const { t } = useI18n();
  const [method, setMethod] = useState('wave');
  const [txid, setTxid] = useState('');
  const [copied, setCopied] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [waveAuto, setWaveAuto] = useState(false);
  const [omAuto, setOmAuto] = useState(false);
  const MERCHANT = { wave: order.seller_wave_number || '', orange_money: order.seller_orange_number || '' };

  useEffect(() => { api.get('/payments/status').then(({ data }) => { setWaveAuto(!!data.wave); setOmAuto(!!data.orange); }).catch(() => {}); }, []);

  const payAuto = async (provider) => {
    setSubmitting(true);
    try {
      const { data } = await api.post(`/payments/${provider}/checkout`, { order_id: order.order_id });
      window.location.assign(data.checkout_url);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur de paiement'); setSubmitting(false); }
  };
  const payWave = () => payAuto('wave');

  const copy = (label, val) => {
    navigator.clipboard.writeText(val);
    setCopied(label);
    setTimeout(() => setCopied(''), 1500);
    toast.success(t('copied'));
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      await api.post('/payments/submit', { order_id: order.order_id, method, transaction_id: txid.trim() });
      toast.success(t('payment_submitted'));
      onSubmitted && onSubmitted();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Error'); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="card-elev rounded-2xl p-5 space-y-4" data-testid="payment-block">
      <div className="flex items-center gap-2 text-amber-400">
        <Wallet className="h-5 w-5" />
        <h3 className="font-display text-lg font-bold">{t('payment_instructions')}</h3>
      </div>
      <div className="text-slate-300 text-sm">
        <div>{order.product_name} — <span className="text-amber-400 font-mono-num">{fcfa(order.amount)}</span></div>
        <div className="text-xs text-slate-500">{t('payment_deadline')}: {new Date(order.payment_deadline).toLocaleString()}</div>
      </div>
      {(waveAuto || omAuto) && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="auto-pay-options">
          {waveAuto && (
            <Button onClick={payWave} disabled={submitting} data-testid="wave-auto-pay-btn"
              className="w-full bg-[#00abec] hover:bg-[#0096d1] text-white font-bold py-6">
              <ShieldCheck className="h-4 w-4 mr-2" />Payer avec Wave
            </Button>
          )}
          {omAuto && (
            <Button onClick={() => payAuto('orange')} disabled={submitting} data-testid="om-auto-pay-btn"
              className="w-full bg-[#ff7900] hover:bg-[#e66d00] text-white font-bold py-6">
              <ShieldCheck className="h-4 w-4 mr-2" />Payer avec Orange Money
            </Button>
          )}
        </div>
      )}
      {(waveAuto || omAuto) && <div className="text-center text-xs text-slate-500">Paiement sécurisé via la plateforme · ou paiement manuel :</div>}
      <div className="grid grid-cols-2 gap-2">
        <button
          data-testid="wave-payment-option"
          onClick={() => setMethod('wave')}
          className={`p-3 rounded-xl border-2 flex items-center gap-2 transition ${method === 'wave' ? 'border-cyan-400 bg-cyan-500/10' : 'border-slate-700 hover:border-slate-500'}`}>
          <Smartphone className="h-4 w-4 text-cyan-400" /><span>Wave</span>
        </button>
        <button
          data-testid="om-payment-option"
          onClick={() => setMethod('orange_money')}
          className={`p-3 rounded-xl border-2 flex items-center gap-2 transition ${method === 'orange_money' ? 'border-orange-500 bg-orange-500/10' : 'border-slate-700 hover:border-slate-500'}`}>
          <Smartphone className="h-4 w-4 text-orange-400" /><span>Orange Money</span>
        </button>
      </div>
      <div className="p-3 bg-slate-900/60 rounded-xl border border-slate-800 flex items-center justify-between">
        <div>
          <div className="text-xs text-slate-500 uppercase tracking-widest">Numéro du vendeur {order.seller_name ? `· ${order.seller_name}` : ''}</div>
          <div className="font-mono-num text-lg" data-testid="seller-pay-number">{MERCHANT[method] || <span className="text-sm text-amber-400">Non renseigné — utilisez « Relancer vendeur » ou le chat du live</span>}</div>
        </div>
        {MERCHANT[method] && <Button variant="outline" size="sm" data-testid="copy-merchant-btn"
          onClick={() => copy('merchant', MERCHANT[method])}
          className="border-slate-700 text-slate-200 hover:bg-slate-800">
          {copied === 'merchant' ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
        </Button>}
      </div>
      <ol className="text-xs text-slate-400 space-y-1 list-decimal pl-4" data-testid="manual-pay-steps">
        <li>Ouvrez {method === 'wave' ? 'Wave' : 'Orange Money'} et envoyez <span className="text-amber-300 font-mono-num">{fcfa(order.amount)}</span> au numéro du vendeur ci-dessus.</li>
        <li>Revenez ici et cliquez sur « J'ai payé ». Le vendeur vérifie la réception puis confirme.</li>
      </ol>
      <input
        data-testid="txid-input"
        value={txid} onChange={(e) => setTxid(e.target.value)}
        placeholder="Référence du paiement (optionnel, si vous l'avez reçue par SMS)"
        className="w-full px-4 py-3 rounded-xl bg-slate-900 border border-slate-700 text-white placeholder-slate-500 focus:border-sky-400 outline-none text-sm" />
      <Button onClick={submit} disabled={submitting} data-testid="submit-payment-btn"
        className="w-full bg-emerald-600 hover:bg-emerald-500 text-white font-bold py-6">
        <Zap className="h-4 w-4 mr-2" />J'ai payé — prévenir le vendeur
      </Button>
    </div>
  );
}
